import {EnumType, randomEnum, useGameServerConnection, usePlayers} from '@glark-newco/game-library';
import {useCallback, useEffect} from 'react';
import {usePlayerAvatar, usePlayerAvatars} from '../state/PlayerAvatarSlice';
import {PlayerAvatarChoiceEvent, SupplyChainPayload} from '../SupplyChainPayloadTypes';

// Stops multiple avatars being generated due to usePlayerAvatars hook being used in multiple places in the UI
let initialAvatarGenerated: boolean = false;

export enum BaseType { A = 1, B, C, D }

export enum ShirtColor {
    ShirtPink = 1,
    ShirtPlaid,
    ShirtWhite,
    TShirtBlue,
    TShirtGreen,
    TShirtPurple,
    TShirtRock,
    TShirtTieDye,
    TShirtWhite,
}

export enum TieColor {
    None,
    BowtieBlack,
    BowtieBlue,
    BowtieRed,
    BowtieYellow,
    NecktieBlack,
    NecktieBlue,
    NecktieRed,
    NecktieTeal,
    NecktieYellow,
}

export enum Overgarment {
    None,
    HiVis,
    LabcoatBlue,
    Nurse,
    SuitJacketBlack,
    SuitJacketBlue,
}

export enum Accessories {
    None,
    DocScope,
    Microphone,
    MP3,
    Satchel,
    Skateboard,
    Toolbelt,
}

export enum Hats {
    None,
    Builder,
    Cap,
    Cowboy,
    Fire,
    Mask,
    Police,
    Turban,
}

export type AvatarOptions = 'BaseType' | 'ShirtColor' | 'TieColor' | 'Overgarment' | 'Accessories' | 'Hats';

export const categories: Record<string, EnumType>  = {BaseType, ShirtColor, TieColor, Overgarment, Accessories, Hats};

export type AvatarInterface = {
    [K in AvatarOptions]: number;
};

export const blankAvatar: AvatarInterface = {
    'BaseType': BaseType.A,
    'ShirtColor': ShirtColor.ShirtPink,
    'TieColor': TieColor.None,
    'Overgarment': Overgarment.None,
    'Accessories': Accessories.None,
    'Hats': Hats.None,
};

export function generateRandomAvatar(): AvatarInterface {
    return {
        BaseType: randomEnum(BaseType) as number,
        ShirtColor: randomEnum(ShirtColor) as number,
        TieColor: randomEnum(TieColor) as number,
        Overgarment: randomEnum(Overgarment) as number,
        Accessories: randomEnum(Accessories) as number,
        Hats: randomEnum(Hats) as number,
    };
}

const getShirtAsset = (shirt: number, bodySuffix: string) => {
    if (!shirt) return undefined;
    return `${ShirtColor[shirt]}${bodySuffix}`;
};

const getTieAsset = (tie: number, bodySuffix: string) => {
    if (!tie) return undefined;
    if (bodySuffix === 'D' && tie >= (TieColor.NecktieBlack as number)) {
        return `${TieColor[tie]}${bodySuffix}`;
    }
    return `${TieColor[tie]}`;
};

const getOvergarmentAsset = (overgarment: number, bodySuffix: string) => {
    if (!overgarment) return undefined;
    return `${Overgarment[overgarment]}${bodySuffix}`;
};

const getAccessoryAsset = (accessory: number, bodySuffix: string) => {
    if (!accessory) return undefined;
    if ((bodySuffix === 'A' || bodySuffix === 'B') && accessory === (Accessories.MP3 as number)) {
        return `${Accessories[accessory]}${bodySuffix}`;
    }
    return `${Accessories[accessory]}`;
};

const getHatAsset = (hat: number) => {
    if (!hat) return undefined;
    return `${Hats[hat]}`;
};

export function resolveOption(base: number, category: string, option: number): string | undefined {
    const bodySuffix = BaseType[base];

    switch (category) {
        case 'BaseType':
            return `Base${bodySuffix}`;
        case 'ShirtColor':
            return getShirtAsset(option, bodySuffix);
        case 'TieColor':
            return getTieAsset(option, bodySuffix);
        case 'Overgarment':
            return getOvergarmentAsset(option, bodySuffix);
        case 'Accessories':
            return getAccessoryAsset(option, bodySuffix);
        case 'Hats':
            return getHatAsset(option);
        default:
            throw new Error(`Unknown category: ${category}`);
    }
}

export function lookup(a: AvatarInterface): Record<AvatarOptions, string | undefined> {
    const resolve = (category: string, option: number) => resolveOption(a.BaseType, category, option);
    return {
        BaseType: resolve('BaseType', a.BaseType),
        ShirtColor: resolve('ShirtColor', a.ShirtColor),
        TieColor: resolve('TieColor', a.TieColor),
        Overgarment: resolve('Overgarment', a.Overgarment),
        Accessories: resolve('Accessories', a.Accessories),
        Hats: resolve('Hats', a.Hats),
    };
}

export function useCreateAvatar() {
    const {connectionReady} = useGameServerConnection();
    const {publishPlayerAction} = useGameServerConnection<SupplyChainPayload>();
    const {localPlayer} = usePlayers();
    const currentAvatar = usePlayerAvatar(localPlayer.playerId);
    const avatars = usePlayerAvatars();

    const publishAvatar = useCallback((avatar: AvatarInterface) => {
        publishPlayerAction({
            type: 'playerAvatarChoiceEvent',
            avatar,
        } as PlayerAvatarChoiceEvent);
    }, []);

    // console.log(`useCreateAvatar - connectionReady: ${connectionReady}, localPlayer: ${localPlayer?.playerId}, avatars:`, avatars);
    useEffect(() => {
        if (connectionReady && localPlayer && localPlayer.playerId) {
            if (!Object.keys(avatars).includes(localPlayer.playerId) && !initialAvatarGenerated) {
                const randomAvatar = generateRandomAvatar();
                publishAvatar(randomAvatar);
                initialAvatarGenerated = true;
            }
        }
    }, [localPlayer, localPlayer.playerId, connectionReady, currentAvatar]);

    return {publishAvatar};
}
